import { useEffect, useRef, useState, useCallback } from 'react'
import type { OperationLogLine, OperationStatus } from '@/core/infra_types'
import { getConfig } from '@/core/infra_runtime_env'

const GATEWAY_URL = getConfig('VITE_API_URL', 'http://localhost:8090') || '/api'

export type ConnectionState = 'idle' | 'streaming' | 'completed' | 'error'

interface UseOperationLogsOptions {
  operationId: string
  enabled?: boolean
}

interface UseOperationLogsResult {
  logs: OperationLogLine[]
  isConnected: boolean
  terminalStatus: OperationStatus | null
  connectionState: ConnectionState
  error: string | null
  reconnect: () => void
}

/**
 * SSE hook for live operation log streaming.
 * Connects to /v1/infra/operations/{id}/logs/stream via Server-Sent Events.
 */
export function useOperationLogs({
  operationId,
  enabled = true,
}: UseOperationLogsOptions): UseOperationLogsResult {
  const [logs, setLogs] = useState<OperationLogLine[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [terminalStatus, setTerminalStatus] = useState<OperationStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const terminalStatusRef = useRef<string | null>(null)

  const connect = useCallback(() => {
    if (!operationId || !enabled) return

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const url = `${GATEWAY_URL}/v1/infra/operations/${operationId}/logs/stream`
    const eventSource = new EventSource(url, { withCredentials: true })
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setIsConnected(true)
      setError(null)
    }

    eventSource.onmessage = (event) => {
      try {
        const logLine = JSON.parse(event.data as string) as OperationLogLine
        setLogs(prev => [...prev, logLine])
      } catch {
        // Non-JSON message, treat as plain text log
        setLogs(prev => [
          ...prev,
          {
            timestamp: new Date().toISOString(),
            level: 'info',
            message: event.data as string,
          },
        ])
      }
    }

    eventSource.onerror = () => {
      // Only set error if we haven't received a terminal status
      // (browser may fire onerror during normal close)
      if (!terminalStatusRef.current) {
        setIsConnected(false)
        setError('Connection lost. Retrying...')
      }
    }

    // Listen for completion event to close stream
    eventSource.addEventListener('done', (event) => {
      const status = (event as MessageEvent).data as string
      terminalStatusRef.current = status
      setTerminalStatus(status as OperationStatus)
      setIsConnected(false)
      eventSource.close()
    })
  }, [operationId, enabled])

  useEffect(() => {
    connect()

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [connect])

  const reconnect = useCallback(() => {
    setLogs([])
    setError(null)
    setTerminalStatus(null)
    terminalStatusRef.current = null
    connect()
  }, [connect])

  const connectionState: ConnectionState =
    terminalStatus ? 'completed' :
    error ? 'error' :
    isConnected ? 'streaming' :
    'idle'

  return { logs, isConnected, terminalStatus, connectionState, error, reconnect }
}
